import { Link, useParams } from 'react-router-dom';
import { useBookings, outcomeOf, attendeeOutcome } from '../api/use-bookings.js';
import { athleteLessonLink } from '../api/lesson-link.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * One booking that holds more than one child — when, where, and who.
 *
 * Reached from the lessons list. It reads the same list rather than a route of
 * its own: there is nothing here the list does not already carry, and a second
 * route would return the same fields and drift from them.
 *
 * A ROW PER CHILD (ruled 12 October). Each child has their own lesson record,
 * so each is a row opening their own page — the note, the plan, and Cancel
 * are there, one place for each. A cancelled child stays a row, saying so:
 * the record is the point. A booking with one child never shows this page;
 * the list opens that child's page directly (lesson-link.ts).
 *
 * NOT HERE, AND NOT PRETENDED: video (LS-06) and the conversation (LS-14)
 * have nothing behind them at all. Each arrives when it exists.
 */
export function LessonRoute() {
  const { bookingId } = useParams();
  const { state } = useBookings();

  if (state.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;

  const lesson = state.lessons.find((candidate) => candidate.booking_id === bookingId);

  if (!lesson) {
    return (
      <section className="panel">
        <h2>Not one of yours</h2>
        <p className="empty">No lesson with that link is on your account.</p>
        <Link className="primary" to="/lessons">
          Back to your lessons
        </Link>
      </section>
    );
  }

  const outcome = outcomeOf(lesson);

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/lessons" aria-label="Back to your lessons">
          ‹
        </Link>
        <div>
          <h2>{when(lesson.slot_start)}</h2>
          <span className="detail">{outcome}</span>
        </div>
      </div>

      <div className="fact">
        <span className="label">Coach</span>
        <span className="value">{lesson.coach_name || '—'}</span>
      </div>
      <div className="fact">
        <span className="label">Lesson</span>
        <span className="value">{lesson.product_name ?? lesson.service_name}</span>
      </div>
      <div className="fact">
        <span className="label">Where</span>
        <span className="value">{lesson.facility_name}</span>
      </div>

      <span className="section-label">Athletes</span>
      {lesson.attendees.map((attendee) => {
        const theirs = attendeeOutcome(lesson, attendee);
        return attendee.lesson_id ? (
          <Link
            key={attendee.client_id}
            className="hour-row"
            to={athleteLessonLink(lesson.booking_id, attendee.lesson_id)}
          >
            <span className="hour-row-text">
              <span className="name">{attendee.name}</span>
            </span>
            <span className={`outcome ${theirs.replace(' ', '-').toLowerCase()}`}>{theirs}</span>
            <span className="chevron" aria-hidden="true">
              ›
            </span>
          </Link>
        ) : null;
      })}
    </>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
